import { Routes } from '@angular/router';

import { forcePasswordChangeGuard } from './core/auth/force-password-change.guard';
import { dirtyFormGuard } from './core/guards/dirty-form.guard';
import { authGuard } from './core/guards/auth.guard';

export const routes: Routes = [
  // -------------------------------------------------------------------------
  // Public (no auth) — demo catalog and exhausted screens (WF-06/07)
  // -------------------------------------------------------------------------
  {
    path: 'demo',
    loadComponent: () =>
      import('./ui/layouts/public-shell/public-shell.layout').then((m) => m.PublicShellLayout),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/exams/catalog/demo-catalog.page').then((m) => m.DemoCatalogPage),
      },
      {
        path: 'exhausted',
        loadComponent: () =>
          import('./features/exams/catalog/demo-exhausted.page').then((m) => m.DemoExhaustedPage),
      },
    ],
  },
  // -------------------------------------------------------------------------
  // Public (no auth) — public registration wizard
  // -------------------------------------------------------------------------
  {
    path: 'register',
    loadComponent: () =>
      import('./ui/layouts/public-shell/public-shell.layout').then(
        (m) => m.PublicShellLayout,
      ),
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/register/register.routes').then((m) => m.registerRoutes),
      },
    ],
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.page').then((m) => m.LoginPage),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./features/auth/forgot-password/forgot-password.page').then(
        (m) => m.ForgotPasswordPage,
      ),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./features/auth/reset-password/reset-password.page').then(
        (m) => m.ResetPasswordPage,
      ),
  },
  {
    // Public anonymous homework share-link viewer. Consumes the URL the
    // share-link dialog generates (`/share/homework/{token}`). No auth — the
    // backend endpoint is AllowAny and returns a non-sensitive summary.
    path: 'share/homework/:token',
    loadComponent: () =>
      import('./features/homework/public/share-homework-view.page').then(
        (m) => m.ShareHomeworkViewPage,
      ),
  },
  {
    path: 'portal',
    loadChildren: () =>
      import('./features/parent-portal/parent-portal.routes').then(
        (m) => m.parentPortalRoutes,
      ),
  },
  {
    path: 'student',
    loadChildren: () =>
      import('./features/student-portal/student-portal.routes').then(
        (m) => m.studentPortalRoutes,
      ),
  },
  {
    path: '',
    canActivate: [authGuard],
    canActivateChild: [forcePasswordChangeGuard],
    loadComponent: () =>
      import('./ui/layouts/app-shell/app-shell.layout').then((m) => m.AppShellLayout),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/home/home.page').then((m) => m.HomePage),
      },
      {
        path: 'me/change-password',
        loadComponent: () =>
          import('./features/me/change-password/change-password.page').then(
            (m) => m.ChangePasswordPage,
          ),
        canDeactivate: [dirtyFormGuard],
        data: { title: 'Change password' },
      },
      {
        // ADR-0081 — destination of the forcePasswordChangeGuard redirect.
        path: 'me/force-change-password',
        loadComponent: () =>
          import('./features/me/force-password-change/force-password-change.page').then(
            (m) => m.ForcePasswordChangePage,
          ),
        data: { title: 'Set a new password' },
      },
      {
        path: 'students',
        loadChildren: () =>
          import('./features/students/students.routes').then((m) => m.studentsRoutes),
      },
      {
        path: 'parents',
        loadChildren: () =>
          import('./features/students/students.routes').then((m) => m.guardiansRoutes),
      },
      {
        path: 'subscriptions',
        loadChildren: () =>
          import('./features/subscriptions/subscriptions.routes').then((m) => m.subscriptionsRoutes),
      },
      {
        path: 'payments',
        loadChildren: () =>
          import('./features/payments/payments.routes').then((m) => m.paymentsRoutes),
      },
      {
        path: 'invoices',
        loadChildren: () =>
          import('./features/payments/payments.routes').then((m) => m.invoicesRoutes),
      },
      {
        path: 'training',
        loadChildren: () =>
          import('./features/training/training.routes').then((m) => m.trainingRoutes),
      },
      {
        path: 'rewards',
        loadChildren: () =>
          import('./features/rewards/rewards.routes').then((m) => m.rewardsRoutes),
      },
      {
        path: 'supplies',
        loadChildren: () =>
          import('./features/supplies/supplies.routes').then((m) => m.suppliesRoutes),
      },
      {
        path: 'inventory',
        loadChildren: () =>
          import('./features/inventory/inventory.routes').then((m) => m.inventoryRoutes),
      },
      {
        path: 'registration',
        loadChildren: () =>
          import('./features/registration/registration.routes').then(
            (m) => m.registrationRoutes,
          ),
      },
      {
        path: 'exams',
        loadChildren: () =>
          import('./features/exams/exams.routes').then((m) => m.examsRoutes),
      },
      {
        path: 'admin',
        loadChildren: () =>
          import('./features/admin/admin.routes').then((m) => m.adminRoutes),
      },
      {
        path: 'inventory',
        loadChildren: () =>
          import('./features/inventory/inventory.routes').then(
            (m) => m.inventoryRoutes,
          ),
      },
      {
        path: 'financials/reports',
        loadChildren: () =>
          import('./features/payments/payments.routes').then(
            (m) => m.financialsReportsRoutes,
          ),
      },
      {
        path: 'competitions/reports',
        loadChildren: () =>
          import('./features/competitions/competitions-reports.routes').then(
            (m) => m.competitionsReportsRoutes,
          ),
      },
      {
        path: 'competitions',
        loadChildren: () =>
          import('./features/competitions/competitions.routes').then(
            (m) => m.competitionsRoutes,
          ),
      },
      {
        path: 'competitions/run',
        loadChildren: () =>
          import('./features/competitions/runner/runner.routes').then(
            (m) => m.competitionsRunnerRoutes,
          ),
      },
      {
        path: 'documents',
        loadChildren: () =>
          import('./features/documents/documents.routes').then(
            (m) => m.documentsRoutes,
          ),
      },
      {
        path: 'homework',
        loadChildren: () =>
          import('./features/homework/homework.routes').then(
            (m) => m.HOMEWORK_ROUTES,
          ),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
